// Entities (SMSF, family trust, company) as records linked to a household.
// Pure: labels, the people attached to an entity, and the review items an
// entity generates (SMSF investment strategy, deed, audit). The review windows
// come from practice settings so a licensee change is a setting, not a release.

export const ENTITY_KINDS = ['smsf', 'trust', 'company', 'other'] as const;
export type EntityKind = (typeof ENTITY_KINDS)[number];

export const ENTITY_ROLES = ['trustee', 'director', 'member', 'beneficiary', 'shareholder', 'appointor', 'secretary', 'guardian'] as const;
export type EntityRole = (typeof ENTITY_ROLES)[number];

export interface EntityPerson {
  role: EntityRole;
  /** A household member when the person is in the household. */
  memberId?: string | null;
  /** Anyone else (an adult child, an outside director). */
  name?: string | null;
  /** Shareholding or membership share, 0..1, where it matters. */
  share?: number | null;
}

export interface EntityRecord {
  id: string;
  clientId: string;
  kind: EntityKind;
  name: string;
  abn: string | null;
  acn: string | null;
  establishedDate: string | null;
  trusteeKind: 'individual' | 'corporate' | null;
  trusteeCompany: string | null;
  deedDate: string | null;
  deedReviewedDate: string | null;
  investmentStrategyReviewedDate: string | null;
  lastAuditDate: string | null;
  auditorName: string | null;
  auditorFirm: string | null;
  people: EntityPerson[];
  notes: string | null;
}

export interface EntityRules {
  /** SMSF investment strategy review, months (SIS reg 4.09 asks for regular review; annual is the norm). */
  strategyReviewMonths: number;
  /** Trust or SMSF deed review, months. */
  deedReviewMonths: number;
  /** SMSF audit counts as overdue this many months after the last audit was signed. */
  auditOverdueMonths: number;
  /** Warn this many days before a review falls due. */
  warnDays: number;
}

export const DEFAULT_ENTITY_RULES: EntityRules = {
  strategyReviewMonths: 12,
  deedReviewMonths: 36,
  auditOverdueMonths: 16,
  warnDays: 30,
};

export const ENTITY_KIND_LABEL: Record<EntityKind, string> = {
  smsf: 'Self-managed super fund', trust: 'Family trust', company: 'Company', other: 'Other entity',
};
export const ENTITY_KIND_SHORT: Record<EntityKind, string> = {
  smsf: 'SMSF', trust: 'Trust', company: 'Company', other: 'Entity',
};
export const ENTITY_ROLE_LABEL: Record<EntityRole, string> = {
  trustee: 'Trustee', director: 'Director', member: 'Member', beneficiary: 'Beneficiary',
  shareholder: 'Shareholder', appointor: 'Appointor', secretary: 'Secretary', guardian: 'Guardian',
};

/** Roles that make sense for each kind, in display order. */
export const ROLES_FOR_KIND: Record<EntityKind, EntityRole[]> = {
  smsf: ['member', 'trustee', 'director'],
  trust: ['trustee', 'director', 'appointor', 'guardian', 'beneficiary'],
  company: ['director', 'shareholder', 'secretary'],
  other: ['trustee', 'director', 'member', 'beneficiary', 'shareholder'],
};

export function addMonthsIso(iso: string, months: number): string {
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  const total = y * 12 + (m - 1) + months;
  const ny = Math.floor(total / 12);
  const nm = (total % 12) + 1;
  const last = new Date(Date.UTC(ny, nm, 0)).getUTCDate();
  return `${String(ny).padStart(4, '0')}-${String(nm).padStart(2, '0')}-${String(Math.min(d, last)).padStart(2, '0')}`;
}

function daysBetween(fromIso: string, toIso: string): number {
  return Math.round((Date.parse(`${toIso}T00:00:00Z`) - Date.parse(`${fromIso}T00:00:00Z`)) / 86_400_000);
}

/** Basic ABN checksum (ATO weighting). Accepts spaces. */
export function isValidAbn(abn: string | null | undefined): boolean {
  if (!abn) return false;
  const digits = abn.replace(/\s+/g, '');
  if (!/^\d{11}$/.test(digits)) return false;
  const w = [10, 1, 3, 5, 7, 9, 11, 13, 15, 17, 19];
  const n = digits.split('').map(Number);
  n[0] -= 1;
  return n.reduce((s, x, i) => s + x * w[i], 0) % 89 === 0;
}

/** Basic ACN check digit (ASIC). Accepts spaces. */
export function isValidAcn(acn: string | null | undefined): boolean {
  if (!acn) return false;
  const digits = acn.replace(/\s+/g, '');
  if (!/^\d{9}$/.test(digits)) return false;
  const n = digits.split('').map(Number);
  const sum = n.slice(0, 8).reduce((s, x, i) => s + x * (8 - i), 0);
  return (10 - (sum % 10)) % 10 === n[8];
}

export type EntityReviewKind = 'strategy' | 'deed' | 'audit';
export type EntityReviewState = 'overdue' | 'dueSoon' | 'missing';

export interface EntityReviewItem {
  entityId: string;
  clientId: string;
  entityName: string;
  kind: EntityReviewKind;
  state: EntityReviewState;
  /** Date it falls (or fell) due; null when there is no date on record. */
  dueDate: string | null;
  detail: string;
}

const REVIEW_LABEL: Record<EntityReviewKind, string> = {
  strategy: 'Investment strategy review', deed: 'Deed review', audit: 'SMSF audit',
};

/**
 * Reviews an entity needs within the warning window, or has missed. Only the
 * kinds that apply: an SMSF has all three, a trust has its deed, a company none.
 */
export function entityReviewItems(e: EntityRecord, today: string, rules: EntityRules = DEFAULT_ENTITY_RULES): EntityReviewItem[] {
  const checks: Array<{ kind: EntityReviewKind; last: string | null; months: number }> = [];
  if (e.kind === 'smsf') {
    checks.push({ kind: 'strategy', last: e.investmentStrategyReviewedDate, months: rules.strategyReviewMonths });
    checks.push({ kind: 'audit', last: e.lastAuditDate, months: rules.auditOverdueMonths });
  }
  if (e.kind === 'smsf' || e.kind === 'trust') {
    checks.push({ kind: 'deed', last: e.deedReviewedDate ?? e.deedDate, months: rules.deedReviewMonths });
  }
  const out: EntityReviewItem[] = [];
  for (const c of checks) {
    const base = { entityId: e.id, clientId: e.clientId, entityName: e.name, kind: c.kind };
    if (!c.last) {
      out.push({ ...base, state: 'missing', dueDate: null, detail: `${REVIEW_LABEL[c.kind]}: no date on record` });
      continue;
    }
    const due = addMonthsIso(c.last, c.months);
    const d = daysBetween(today, due);
    if (d < 0) out.push({ ...base, state: 'overdue', dueDate: due, detail: `${REVIEW_LABEL[c.kind]} overdue ${-d} day${d === -1 ? '' : 's'}` });
    else if (d <= rules.warnDays) out.push({ ...base, state: 'dueSoon', dueDate: due, detail: `${REVIEW_LABEL[c.kind]} due in ${d} day${d === 1 ? '' : 's'}` });
  }
  return out;
}

/** Names of the people in an entity, resolved against household members. */
export function personLabel(p: EntityPerson, members: Array<{ id: string; firstName: string | null; surname: string | null }>): string {
  if (p.memberId) {
    const m = members.find((x) => x.id === p.memberId);
    if (m) return [m.firstName, m.surname].filter(Boolean).join(' ') || 'Household member';
  }
  return p.name?.trim() || 'Unnamed';
}
